import { AsyncPipe, CurrencyPipe, DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, Input, OnChanges, SimpleChanges, ViewChild, inject } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { InstrumentDetailsLinkComponent } from '@zero/shared/ui-web-components';
import { Dividend, DividendType } from '@zero/statistics/domain';
import { ChartConfiguration, ChartData } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { BaseChartDirective } from 'ng2-charts';
import { BehaviorSubject, Observable, filter, map } from 'rxjs';
import { DividendByPeriodPipe } from '../../pipes/dividend-by-period.pipe';
import { FilterEmptyDividendPipe } from '../../pipes/filter-empty-dividend.pipe';
@Component({
  selector: 'statistics-dividends',
  templateUrl: './dividends.component.html',
  styleUrls: ['../../styles/statistics.scss', './dividends.component.scss'],
  imports: [
    TranslocoPipe,
    DividendByPeriodPipe,
    FilterEmptyDividendPipe,
    InstrumentDetailsLinkComponent,
    NgTemplateOutlet,
    NgClass,
    AsyncPipe,
    CurrencyPipe,
    BaseChartDirective,
  ],
  providers: [DatePipe, CurrencyPipe],
})
export class DividendsComponent implements AfterViewInit, OnChanges {
  @Input() public dividends: Dividend[];

  public readonly DividendType = DividendType;
  public activeDividendType = DividendType.Forecast;
  public shouldDisplayDetails = false;
  public labels: PeriodLabel[];
  public total: number;

  public data = new Observable<ChartData<'bar'>>();
  public options: ChartConfiguration['options'];
  public readonly plugins = [ChartDataLabels];

  readonly #datePipe = inject(DatePipe);
  readonly #currencyPipe = inject(CurrencyPipe);
  readonly #chartData = new BehaviorSubject<Map<string, number>>(undefined);

  @ViewChild(BaseChartDirective) public chart: BaseChartDirective | undefined;

  public ngAfterViewInit(): void {
    this.#setChartOptions();
    this.#createChartData();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    changes['dividends']?.currentValue && this.#setChartData();
  }

  #toNiceLabel(label: string): string {
    if (!isNaN(Number(label))) {
      return label;
    }

    try {
      const date = label.split('-');
      return this.#datePipe.transform(new Date(Number(date[0]), Number(date[1])), 'MMMM yy', 'UTC');
    } catch (e) {
      return label;
    }
  }

  #setChartOptions(): void {
    this.options = {
      layout: {
        padding: {
          top: 20,
        },
      },
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          clamp: true,
          font: {
            family: 'Work Sans',
          },
          color: '#02172d',
          anchor: 'end',
          align: 'end',
          formatter: (value) => this.#currencyPipe.transform(value),
        },
        tooltip: {
          enabled: false,
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
        },
        y: {
          display: false,
        },
      },
    };
  }

  #setChartData(): void {
    let data = new Map<string, number>();
    this.dividends.forEach(({ forecastedPeriods, historicalPeriods }) => {
      const activePeriod = this.activeDividendType === DividendType.Forecast ? forecastedPeriods : historicalPeriods;
      if (!activePeriod?.length) {
        return;
      }

      activePeriod.forEach(({ period, dividendAbsolute }) => {
        data.set(period, data.has(period) ? data.get(period) + dividendAbsolute : dividendAbsolute);
      });
    });

    data = new Map([...data.entries()].sort((a, b) => a[0].localeCompare(b[0])));

    this.labels = [...data.keys()].map((period) => ({
      label: this.#toNiceLabel(period),
      period,
    }));

    this.total = Array.from(data.values()).reduce((acc, val) => acc + val, 0);

    this.#chartData.next(data);
  }

  #createChartData(): void {
    this.data = this.#chartData.pipe(
      filter((data) => !!data?.values()),
      map((chartData) => ({
        labels: this.labels?.map(({ label }) => label),
        datasets: [
          {
            data: [...chartData.values()],
            backgroundColor: '#248eff',
            barThickness: 65,
          },
        ],
      })),
    );
  }

  public changeType(dividendType: DividendType): void {
    this.activeDividendType = dividendType;
    this.#setChartData();
  }
}

interface PeriodLabel {
  period: string;
  label: string;
}
